/**
 * Helper functions for checking IELTS test scores and AI scoring
 */

/**
 * Parse analytics from string or object
 * @param {Object|string} analytics - Analytics object or JSON string
 * @returns {Object} Parsed analytics object
 */
export const parseAnalytics = (analytics) => {
  if (!analytics) return {};
  if (typeof analytics === 'string') {
    try {
      return JSON.parse(analytics);
    } catch (e) {
      return {};
    }
  }
  return analytics || {};
};

/**
 * Check if AI scoring exists (even if confidence is low or band is 0)
 * The AI backend returns a response structure even when it "fails" (e.g., insufficient word count),
 * so we check for the presence of overall_band in ai_scoring rather than just status
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if AI scoring exists (regardless of confidence or band score)
 */
export const hasAIScore = (apiResult) => {
  if (!apiResult?.response) return false;
  const analytics = parseAnalytics(apiResult.response.analytics);
  // Check if ai_scoring exists and has overall_band defined (even if 0)
  // This handles cases where AI returns results even on "failure" (low confidence, insufficient content, etc.)
  return !!(analytics?.ai_scoring && analytics.ai_scoring.overall_band !== undefined && analytics.ai_scoring.overall_band !== null);
};

/**
 * Check if a score is available (handles 0 correctly)
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if score is available (including 0)
 */
export const hasScore = (apiResult) => {
  if (!apiResult?.response) return false;
  const totalMarksObtained = apiResult.response.totalMarksObtained;
  const status = apiResult.response.status;
  
  // Check if totalMarksObtained exists (even if 0)
  const hasTotalMarks = totalMarksObtained !== null && 
                        totalMarksObtained !== undefined && 
                        totalMarksObtained !== '';
  
  // Also check for AI scoring
  const hasAI = hasAIScore(apiResult);
  
  return (hasTotalMarks || hasAI) && status !== 'pending_evaluation';
};

/**
 * Round IELTS bands up to the next half band.
 * Examples: 4.4 -> 4.5, 4.5 -> 4.5, 4.6 -> 5
 * @param {number|string} score - Raw IELTS band score
 * @returns {number|null} Rounded IELTS band score or null if unavailable
 */
export const roundIELTSBandScore = (score) => {
  if (score === null || score === undefined || score === '') return null;

  const numericScore = Number(score);
  if (Number.isNaN(numericScore)) return null;

  const roundedScore = Math.ceil((numericScore * 2) - Number.EPSILON) / 2;
  return Math.min(9, Math.max(0, roundedScore));
};

/**
 * Format IELTS band scores after applying the shared rounding rule.
 * @param {number|string} score - Raw IELTS band score
 * @param {string} fallback - Display value when score is unavailable
 * @returns {string} Formatted band score
 */
export const formatIELTSBandScore = (score, fallback = '-') => {
  const roundedScore = roundIELTSBandScore(score);
  if (roundedScore === null) return fallback;

  return Number.isInteger(roundedScore) ? String(roundedScore) : roundedScore.toFixed(1);
};

/**
 * Get the manual/teacher band score from totalMarksObtained.
 * @param {Object} apiResult - API result object with response
 * @returns {number|null} Manual band score or null if not available
 */
export const getManualBandScore = (apiResult) => {
  if (!apiResult?.response) return null;

  const totalMarksObtained = apiResult.response.totalMarksObtained;

  if (totalMarksObtained !== null && totalMarksObtained !== undefined && totalMarksObtained !== '') {
    return roundIELTSBandScore(totalMarksObtained);
  }

  return null;
};

/**
 * Check whether a score was reviewed/overridden by a teacher.
 * Prefer explicit backend flags when available; fall back to detecting a manual
 * score that differs from the AI score for older responses.
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if teacher review should be shown
 */
export const hasTeacherReviewedScore = (apiResult) => {
  if (!apiResult?.response) return false;

  const response = apiResult.response;
  const analytics = parseAnalytics(response.analytics);
  const manualBandScore = getManualBandScore(apiResult);

  const explicitTeacherReview =
    response.teacherReviewed === true ||
    response.teacher_reviewed === true ||
    response.manualScoring === true ||
    response.manual_scoring === true ||
    analytics.teacherReviewed === true ||
    analytics.teacher_reviewed === true ||
    analytics.manualScoring === true ||
    analytics.manual_scoring === true;

  if (explicitTeacherReview && manualBandScore !== null) {
    return true;
  }

  const aiBandScore = analytics?.ai_scoring?.overall_band;
  if (manualBandScore !== null && aiBandScore !== undefined && aiBandScore !== null) {
    const roundedAiScore = roundIELTSBandScore(aiBandScore);
    return roundedAiScore !== null && roundedAiScore !== manualBandScore;
  }

  return false;
};

/**
 * Get the band score (from AI scoring or totalMarksObtained)
 * @param {Object} apiResult - API result object with response
 * @param {Object} options - Score selection options
 * @param {boolean} options.preferManualScore - Whether teacher/manual score should override AI score
 * @returns {number|null} Band score or null if not available
 */
export const getBandScore = (apiResult, options = {}) => {
  if (!apiResult?.response) return null;
  
  const { preferManualScore = false } = options;
  const analytics = parseAnalytics(apiResult.response.analytics);
  const aiScoring = analytics?.ai_scoring;
  const manualBandScore = getManualBandScore(apiResult);

  if (preferManualScore && manualBandScore !== null) {
    return manualBandScore;
  }
  
  // Prefer AI scoring band if available
  if (aiScoring?.overall_band !== undefined && aiScoring.overall_band !== null) {
    return roundIELTSBandScore(aiScoring.overall_band);
  }
  
  return manualBandScore;
};

/**
 * Check if test status is pending evaluation
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if status is pending_evaluation
 */
export const isPending = (apiResult) => {
  if (!apiResult?.response) return false;
  return apiResult.response.status === 'pending_evaluation';
};

/**
 * Check if test is completed
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if status is completed
 */
export const isCompleted = (apiResult) => {
  if (!apiResult?.response) return false;
  return apiResult.response.status === 'completed';
};

/**
 * Get AI scoring object from apiResult
 * @param {Object} apiResult - API result object with response
 * @returns {Object|null} AI scoring object or null
 */
export const getAIScoring = (apiResult) => {
  if (!apiResult?.response) return null;
  const analytics = parseAnalytics(apiResult.response.analytics);
  return analytics?.ai_scoring || null;
};

/**
 * Check if score should be displayed (for Writing/Speaking with AI scoring or any score)
 * @param {Object} apiResult - API result object with response
 * @returns {boolean} True if score should be displayed
 */
export const shouldShowScore = (apiResult) => {
  if (!apiResult?.response) return false;
  
  // Check for AI scoring (even if band is 0)
  const hasAI = hasAIScore(apiResult);
  
  // Check for totalMarksObtained (handles 0 correctly)
  const totalMarksObtained = apiResult.response.totalMarksObtained;
  const hasTotalMarks = totalMarksObtained !== null && 
                        totalMarksObtained !== undefined && 
                        totalMarksObtained !== '';
  
  const status = apiResult.response.status;
  
  return (hasAI || hasTotalMarks) && status !== 'pending_evaluation';
};
